import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RequestAccessModal } from "@/components/RequestAccessModal";
import { 
  ArrowLeft, 
  ArrowRight,
  Radio, 
  Gamepad2,
  Shield,
  Sparkles,
  FileText,
  Pencil,
  Rocket
} from "lucide-react";

export default function AdminArea() {
  const navigate = useNavigate();
  const [showAspiringRequest, setShowAspiringRequest] = useState(false);

  return (
    <div className="min-h-screen bg-[#121318] flex flex-col">
      {/* Navigation */}
      <nav className="border-b border-slate-700 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 font-medium">Admin Area</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-white mb-3">
            Operon Admin Tools
          </h1>
          <p className="text-slate-400">
            Private tools and experiences for demos and testing
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Homepage Editor Card */}
          <Card 
            onClick={() => navigate('/admin-area/homepage-editor')}
            className="bg-gradient-to-br from-teal-900/30 to-slate-800/50 border-teal-500/30 hover:border-teal-500/60 cursor-pointer transition-all hover:shadow-xl hover:shadow-teal-500/10 group"
          >
            <CardHeader>
              <div className="w-14 h-14 rounded-xl bg-teal-500/20 flex items-center justify-center mb-3 group-hover:bg-teal-500/30 transition-colors">
                <Pencil className="w-7 h-7 text-teal-400" />
              </div>
              <CardTitle className="text-white text-xl">Homepage Editor</CardTitle>
              <CardDescription className="text-slate-400">
                Edit the text content displayed on the main homepage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-slate-400 text-sm space-y-2 mb-4">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                  Hero section titles
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                  Subtitle and descriptions
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                  AI Tools section
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                  Aspiring founders section
                </li>
              </ul>
              <div className="flex items-center text-teal-400 text-sm font-medium group-hover:gap-2 transition-all">
                <span>Edit Content</span>
                <FileText className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>

          {/* Operon Live Card */}
          <Card 
            onClick={() => navigate('/ai-personas')}
            className="bg-gradient-to-br from-purple-900/30 to-slate-800/50 border-purple-500/30 hover:border-purple-500/60 cursor-pointer transition-all hover:shadow-xl hover:shadow-purple-500/10 group"
          >
            <CardHeader>
              <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-3 group-hover:bg-purple-500/30 transition-colors">
                <Radio className="w-7 h-7 text-purple-400" />
              </div>
              <CardTitle className="text-white text-xl">Operon Live</CardTitle>
              <CardDescription className="text-slate-400">
                AI Voice Personas for pitch practice
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-slate-400 text-sm space-y-2 mb-4">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                  Max Returns - AI Investor
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                  Diana Pipeline - Pharma
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                  Sergej Panél - Moderator
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                  Alex Chen - Attendee
                </li>
              </ul>
              <div className="flex items-center text-purple-400 text-sm font-medium group-hover:gap-2 transition-all">
                <span>Enter Operon Live</span>
                <Sparkles className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>

          {/* Science Invaders Card */}
          <Card 
            onClick={() => navigate('/science-invaders')}
            className="bg-gradient-to-br from-pink-900/30 to-slate-800/50 border-pink-500/30 hover:border-pink-500/60 cursor-pointer transition-all hover:shadow-xl hover:shadow-pink-500/10 group"
          >
            <CardHeader>
              <div className="w-14 h-14 rounded-xl bg-pink-500/20 flex items-center justify-center mb-3 group-hover:bg-pink-500/30 transition-colors">
                <Gamepad2 className="w-7 h-7 text-pink-400" />
              </div>
              <CardTitle className="text-white text-xl">Science Invaders</CardTitle>
              <CardDescription className="text-slate-400">
                Arcade game - founders vs investors
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-slate-400 text-sm space-y-2 mb-4">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400"></span>
                  Phased combat
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400"></span>
                  Strategic weapons
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400"></span>
                  Educational dialogues
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400"></span>
                  High score leaderboard
                </li>
              </ul>
              <div className="flex items-center text-pink-400 text-sm font-medium group-hover:gap-2 transition-all">
                <span>Play Now</span>
                <span className="ml-1">🎮</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Aspiring Founders Access Card (moved from Homepage) */}
        <div className="mt-12">
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl p-8 md:p-12 border border-purple-500/30 text-center">
            <Rocket className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Are you an aspiring founder?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Access Operon Start to understand how to make the jump into UK therapeutics. Find venture builders, connect with co-founders, and assess your readiness.
            </p>
            <Button
              onClick={() => setShowAspiringRequest(true)}
              className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-6 h-auto text-lg rounded-xl"
              data-testid="btn-aspiring"
            >
              Access Operon Start
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </main>

      {/* Request Access Modal (Aspiring Founders) */}
      <RequestAccessModal
        open={showAspiringRequest}
        onOpenChange={setShowAspiringRequest}
        feature="aspiring"
      />
    </div>
  );
}
